import {
  getDefaultControlValue,
  getUnifiedValue,
} from '@src/utility/compositionSettings';
import { LogManager } from '@src/utility/Logger';

import type { CompositionTemplate } from '@src/components/sidepanel/CompositionSettings/types';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

export class CompositionThumbnailService {
  private videoCreator: IVideoCreatorStore;
  private logger = LogManager.getLogger('service:CompositionThumbnailService');
  constructor(videoCreator: IVideoCreatorStore) {
    this.videoCreator = videoCreator;
  }

  /**
   * Recursively navigates through nested elements and sets a value at the target path.
   *
   * @param current - Current element in the navigation
   * @param path - Remaining path indices to navigate
   * @param property - Property name to set
   * @param value - Value to set at the target
   * @returns true if value was set successfully, false otherwise
   */
  private applyValueAtPath(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    current: any,
    path: number[],
    property: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
  ): boolean {
    // Validate current object has elements array
    if (!current || !current.elements || !Array.isArray(current.elements)) {
      return false;
    }

    const [index, ...remainingPath] = path;

    // Validate index
    if (
      typeof index !== 'number' ||
      index < 0 ||
      index >= current.elements.length
    ) {
      return false;
    }

    const element = current.elements[index];

    // Validate element exists and is an object
    if (!element || typeof element !== 'object') {
      return false;
    }

    // Base case: we've reached the end of the path
    if (remainingPath.length === 0) {
      element[property] = value;
      return true;
    }

    // Recursive case: navigate deeper
    return this.applyValueAtPath(element, remainingPath, property, value);
  }

  /**
   * Applies control values to the composition source by replacing properties
   * at specified element paths.
   *
   * @param source - The composition source object
   * @param controls - Array of control configurations
   * @returns Modified source object with control values applied
   */
  private applyControlValuesToSource(
    source: Record<string, unknown>,
    controls: CompositionTemplate['controls'],
  ): Record<string, unknown> {
    // Deep clone the source to avoid mutations
    const modifiedSource = JSON.parse(JSON.stringify(source)) as Record<
      string,
      unknown
    >;

    if (!controls || !Array.isArray(controls)) {
      return modifiedSource;
    }

    for (const control of controls) {
      if (
        !control ||
        !control.elementPaths ||
        control.enabled === false ||
        !Array.isArray(control.elementPaths)
      ) {
        continue;
      }

      // Get the value to apply (customValue takes precedence over default)
      let valueToApply = control.customValue;
      if (!valueToApply) {
        const { unifiedValue } = getUnifiedValue(
          control,
          this.videoCreator.activeElement,
          this.videoCreator.currentVideo?.extraElementData,
        );
        valueToApply = unifiedValue;
      }

      if (valueToApply === undefined || valueToApply === null) {
        valueToApply = undefined;
      }
      if (
        valueToApply !== null &&
        typeof valueToApply === 'object' &&
        'value' in valueToApply
      ) {
        valueToApply = valueToApply.value;
      } else if (
        valueToApply !== null &&
        typeof valueToApply === 'object' &&
        'brandKit' in valueToApply
      ) {
        valueToApply = getDefaultControlValue(
          control,
          this.videoCreator.organization,
          this.videoCreator.story,
        );
      }

      // Apply value to each element path
      for (const path of control.elementPaths) {
        if (!Array.isArray(path) || path.length === 0 || !valueToApply) {
          continue;
        }

        try {
          const success = this.applyValueAtPath(
            modifiedSource,
            path,
            control.property,
            valueToApply,
          );

          if (!success) {
            this.logger.warn(
              `Failed to apply control value at path [${path.join(
                ', ',
              )}] for property "${control.property}"`,
            );
          }
        } catch (error) {
          this.logger.warn(
            `Error applying control value at path [${path.join(
              ', ',
            )}] for property "${control.property}":`,
            error,
          );
        }
      }
    }

    return modifiedSource;
  }

  /**
   * Generates a thumbnail image for a composition using Creatomate.
   *
   * @param composition - The composition template
   * @returns Upload ID of the thumbnail in DatoCMS
   */
  async generateCompositionThumbnail(
    composition: CompositionTemplate,
    adjustAspectRatioForComposition?: boolean,
  ): Promise<{ id: string; url: string } | undefined> {
    try {
      const sourceCopy = JSON.parse(JSON.stringify(composition.source));

      /**
       * this is needed to be on a FE part to not replicate much logic in the BE part
       * We grab the source and controls, also getting brand kit values, take something from an editor itself etc.
       * replicating the same logic will just increase overall maintenance complexity
       * */

      const modifiedSource = this.applyControlValuesToSource(
        sourceCopy,
        composition.controls,
      );

      const completedRender = await fetch(
        `${process.env.REACT_APP_API_URL}/api/media/generate`,
        {
          method: 'POST',
          body: JSON.stringify({
            compositionSource: modifiedSource,
            outputFormat: 'png',
            snapshotConfig: {
              removeAnimations: true,
              adjustAspectRatioForComposition,
            },
            datoCMSUpload: {
              enabled: true,
              compositionId: composition.id,
            },
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!completedRender.ok) {
        throw new Error(
          'Failed to generate thumbnail: ' + completedRender.statusText,
        );
      }

      const completedRenderData = await completedRender.json();
      return completedRenderData?.data?.datoMedia;
    } catch (error) {
      this.logger.warn(
        'Failed to generate thumbnail, proceeding without it:',
        error,
      );
      // Return null on error - the composition can still be created without a thumbnail
      return undefined;
    }
  }
}
